// Приём вердиктов клиента СПИСКОМ.
//
// У Lightly нет API: вердикты приходят таблицей — «имя файла, принят или
// отклонён, почему». Отгрузка это десятки клипов, и тыкать каждый руками —
// часы работы. Здесь штаб вставляет таблицу как есть, видит предпросмотр и
// применяет одним нажатием.
//
// Два шага (разобрать → показать → применить) намеренно: в списке от клиента
// почти всегда есть строка, которую мы не нашли — переименованный файл,
// опечатка, клип чужого пула. Узнавать об этом надо ДО того, как деньги
// начислены, а не после.
import { useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api } from '../api';
import type { VistaVerdictsPreviewRow } from '@weern/contracts';
import type { T } from '../i18n';

type Props = { t: T; onClose: () => void; onApplied: () => void };

export function VerdictsModal({ t, onClose, onApplied }: Props) {
  const [text, setText] = useState('');
  const [rows, setRows] = useState<VistaVerdictsPreviewRow[] | null>(null);
  const [ready, setReady] = useState(0);
  const [problems, setProblems] = useState(0);
  const [truncated, setTruncated] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const preview = async () => {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.verdictsPreview(text);
      setRows(res.rows);
      setReady(res.ready);
      setProblems(res.problems);
      setTruncated(res.truncated ?? 0);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const apply = async () => {
    if (!rows) return;
    setBusy(true);
    setErr(null);
    try {
      // Группируем по (действие + причина): у /captures/move одна причина на
      // вызов, а причин у клиента в одном списке бывает пять. Заодно это
      // означает, что каждая группа применяется одним запросом, а не строкой.
      const groups = new Map<string, { move: 'accept' | 'reject'; reason?: string; ids: string[] }>();
      for (const r of rows) {
        if (!r.id || r.ambiguous || !r.move) continue;
        const key = `${r.move}:${r.reason ?? ''}`;
        const cur = groups.get(key);
        if (cur) cur.ids.push(r.id);
        else groups.set(key, { move: r.move, reason: r.reason ?? undefined, ids: [r.id] });
      }

      let moved = 0;
      let accrued = 0;
      let reversed = 0;
      const skipped: string[] = [];
      for (const g of groups.values()) {
        // По 200 за раз — потолок контракта на размер пачки.
        for (let i = 0; i < g.ids.length; i += 200) {
          const res = await api.moveCaptures({
            ids: g.ids.slice(i, i + 200),
            move: g.move,
            ...(g.move === 'reject' ? { reason: (g.reason ?? 'other') as never } : {}),
          });
          moved += res.moved;
          accrued += res.accruedCents;
          reversed += res.reversedCents ?? 0;
          skipped.push(...res.skipped.map((s) => `${s.id}: ${s.reason}`));
        }
      }
      const money =
        accrued || reversed
          ? t('v.accrued', { amount: (accrued / 100).toFixed(2) }) +
            (reversed ? t('v.reversed', { amount: (reversed / 100).toFixed(2) }) : '') +
            '.'
          : '';
      setDone(
        t('v.applied', { n: moved, money }) +
          (skipped.length
            ? t('v.skipped', { n: skipped.length, list: skipped.slice(0, 3).join('; ') })
            : ''),
      );
      setRows(null);
      setText('');
      onApplied();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div class="profile-menu-backdrop" onClick={onClose}>
      <div class="wb-card verdicts-modal" onClick={(e: Event) => e.stopPropagation()}>
        <h3>{t('v.title')}</h3>
        <p class="muted">{t('v.help')}</p>

        {done && <div class="strip info">{done}</div>}
        {err && <div class="strip bad">{err}</div>}

        {rows === null ? (
          <>
            <textarea
              rows={10}
              value={text}
              placeholder={'kitchen_01.mp4\taccepted\nstreet_04.mp4\trejected\ttoo dark'}
              onInput={(e) => setText((e.target as HTMLTextAreaElement).value)}
            />
            <div class="verdicts-actions">
              <button type="button" class="btn-ghost" onClick={onClose}>
                {t('v.close')}
              </button>
              <button
                type="button"
                class="btn-soft"
                disabled={busy || text.trim().length === 0}
                onClick={() => void preview()}
              >
                {busy ? t('v.parsing') : t('v.check')}
              </button>
            </div>
          </>
        ) : (
          <>
            {truncated > 0 && (
              <div class="strip warn">
                {t('v.truncated', { n: truncated })}
              </div>
            )}
            <div class="strip info">
              {t('v.willApply')} <b>{ready}</b>
              {problems > 0 && (
                <>
                  {' '}
                  {t('v.needAttention', { n: problems })}
                </>
              )}
            </div>
            <div class="tablewrap verdicts-preview">
              <table>
                <thead>
                  <tr>
                    <th>{t('v.th.line')}</th>
                    <th>{t('v.th.clip')}</th>
                    <th>{t('v.th.who')}</th>
                    <th>{t('v.th.verdict')}</th>
                    <th>{t('v.th.problem')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const problem = !r.id || r.ambiguous || !r.move;
                    return (
                      <tr key={i} class={problem ? 'row-blocked' : ''}>
                        <td class="mono">{r.raw}</td>
                        <td>{r.fileName ?? <span class="muted">{t('v.notFound')}</span>}</td>
                        <td>{r.contributorName ?? '—'}</td>
                        <td>
                          {r.move === 'accept'
                            ? t('v.accepted')
                            : r.move === 'reject'
                              ? `${t('v.rejected')}${r.reason ? ` · ${t(`v.reason.${r.reason}`)}` : ''}`
                              : '—'}
                        </td>
                        <td class="muted">
                          {r.ambiguous
                            ? t('v.ambiguous')
                            : !r.id
                              ? t('v.notInPool')
                              : !r.move
                                ? t('v.badVerdict')
                                : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div class="verdicts-actions">
              <button type="button" class="btn-ghost" disabled={busy} onClick={() => setRows(null)}>
                {t('v.back')}
              </button>
              <button type="button" class="btn-soft" disabled={busy || ready === 0} onClick={() => void apply()}>
                {busy ? t('v.applying') : t('v.apply', { n: ready })}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
